import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Lock, CheckCircle2, Send, XCircle, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { staffApi, errMsg, getUser } from "@/lib/api";

const STAGES = {
  new: ["Needs review", "bg-amber-100 text-amber-800"],
  proposed: ["Waiting for requester", "bg-sky-100 text-sky-800"],
  declined: ["Requester declined", "bg-rose-100 text-rose-700"],
  confirmed: ["Confirmed", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-slate-100 text-slate-500"],
};
const hm = (t) => { const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const when = (f) => `${f.date ? format(parseISO(f.date), "EEE, MMM d, yyyy") : "No date yet"}${f.time ? ` · ${hm(f.time)}` : ""}`;

const Stage = ({ s, testId }) => <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STAGES[s][1]}`} data-testid={testId}>{STAGES[s][0]}</span>;

const MinisterPicker = ({ r, form, pick }) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    staffApi().get("/pastoral/availability", { params: { service: r.flow.service, date: form.date, time: form.time, skip_event: r.flow.event_id || "" } }).then((x) => setList(x.data)).catch(() => {});
  }, [r.flow.service, r.flow.event_id, form.date, form.time]);
  return (
    <div className="space-y-1.5" data-testid="minister-availability">
      {list.map((m) => (
        <button key={m.id} type="button" onClick={() => pick(m.id)} data-testid={`avail-${m.id}`}
          className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors ${form.minister_id === m.id ? "border-[#0A3A6B] bg-[#EBF3FA]" : "border-slate-200 hover:border-[#1D64B4]"}`}>
          <span className="font-medium text-slate-900">{m.name}{m.id === r.flow.requested_minister_id && <span className="ml-2 text-xs text-[#1D64B4]">requested</span>}</span>
          <span className={`text-xs ${m.available ? "text-emerald-700" : "text-rose-600"}`}>{m.available ? "Available" : m.reason}</span>
        </button>
      ))}
    </div>
  );
};

const RequestDialog = ({ r, onClose, reload }) => {
  const f = r.flow;
  const [form, setForm] = useState({ minister_id: f.minister_id || f.requested_minister_id, date: f.date, time: f.time, venue: f.venue || "", message: "" });
  const [busy, setBusy] = useState(false);
  const act = async (path, body, msg) => {
    setBusy(true);
    try { await staffApi().post(`/pastoral/requests/${r.id}/${path}`, body); toast.success(msg); reload(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const release = async () => { try { await staffApi().delete(`/pastoral/requests/${r.id}/claim`); toast.success("Date released for other admins"); reload(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  const open = ["new", "proposed", "declined", "confirmed"].includes(f.stage);
  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl bg-white sm:max-w-2xl" data-testid="pastoral-request-dialog">
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">{f.service_title} – {r.name} <Stage s={f.stage} testId="dialog-stage" /></DialogTitle>
        <DialogDescription>{r.phone} · {r.email} · requested {f.requested_minister_name || "no particular minister"}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-1 text-xs text-slate-600">
          {Object.entries(r.details || {}).map(([k, v]) => <p key={k}><b>{k}:</b> {v}</p>)}
          <div className="mt-3 border-t pt-3" data-testid="request-history">
            {f.history.map((h, i) => <p key={i} className="text-slate-500">{format(parseISO(h.at), "MMM d, h:mm a")} · {h.by}: {h.text}</p>)}
          </div>
        </div>
        {open && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="confirm-date-input" />
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="confirm-time-input" />
            </div>
            <Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Venue / address" data-testid="confirm-venue-input" />
            <p className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">Ministers for this service</p>
            <MinisterPicker r={r} form={form} pick={(id) => setForm({ ...form, minister_id: id })} />
            <Button disabled={busy || !form.minister_id || !form.date} onClick={() => act("confirm", { minister_id: form.minister_id, date: form.date, time: form.time, venue: form.venue }, "Confirmed, added to calendar, emails sent")}
              className="w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="confirm-request-button"><CheckCircle2 className="mr-1 h-4 w-4" /> {f.stage === "confirmed" ? "Update confirmation" : "Confirm with selected pastor"}</Button>
            {f.stage !== "confirmed" && form.minister_id && form.minister_id !== f.requested_minister_id && (
              <div className="space-y-2 rounded-2xl bg-slate-50 p-3">
                <Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Optional note to the requester" data-testid="propose-message-input" />
                <Button variant="outline" disabled={busy} onClick={() => act("propose", { minister_id: form.minister_id, origin: window.location.origin, message: form.message }, "Email sent asking the requester")}
                  className="w-full rounded-full" data-testid="propose-request-button"><Send className="mr-1 h-4 w-4" /> Ask requester about this pastor</Button>
              </div>
            )}
            {f.stage === "proposed" && (
              <div className="flex gap-2" data-testid="manual-answer">
                <Button size="sm" variant="outline" className="flex-1" disabled={busy} onClick={() => act("answer", { answer: "accept" }, "Recorded: accepted")} data-testid="manual-accept-button">They said yes</Button>
                <Button size="sm" variant="outline" className="flex-1" disabled={busy} onClick={() => act("answer", { answer: "decline" }, "Recorded: declined")} data-testid="manual-decline-button">They said no</Button>
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="flex-1 text-rose-600" disabled={busy} onClick={() => window.confirm("Cancel this request?") && act("cancel", {}, "Request cancelled")} data-testid="cancel-request-button"><XCircle className="mr-1 h-4 w-4" /> Cancel request</Button>
              <Button size="sm" variant="ghost" className="flex-1" onClick={release} data-testid="release-date-button"><Unlock className="mr-1 h-4 w-4" /> Release date</Button>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  );
};

const RequestCard = ({ r, me, onOpen }) => {
  const f = r.flow;
  const mine = r.claim?.admin_id === me?.id;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center" data-testid={`pastoral-req-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-slate-900">{f.service_title} – {r.name}</p><Stage s={f.stage} testId={`pastoral-req-stage-${r.id}`} /></div>
        <p className="mt-1 text-xs text-slate-500">{when(f)} · Requested: {f.requested_minister_name || "no preference"}
          {f.availability && <span className={f.availability.available ? "text-emerald-700" : "text-rose-600"}> ({f.availability.available ? "available" : f.availability.reason})</span>}
          {f.minister_name && ` · Ministering: ${f.minister_name}`}</p>
      </div>
      {r.claim && <span className={`flex items-center gap-1 text-xs ${mine ? "text-[#1D64B4]" : "text-slate-500"}`} data-testid={`pastoral-req-claim-${r.id}`}><Lock className="h-3 w-3" />{mine ? "You're handling this date" : `Handled by ${r.claim.admin_name}`}</span>}
      <Button size="sm" onClick={() => onOpen(r)} disabled={(!!r.claim && !mine) || f.stage === "cancelled"} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid={`pastoral-req-open-${r.id}`}>Open</Button>
    </div>
  );
};

export const PastoralRequests = () => {
  const me = getUser();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("open");
  const [current, setCurrent] = useState(null);
  const load = useCallback(() => { staffApi().get("/pastoral/requests").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const openReq = async (r) => {
    try { await staffApi().post(`/pastoral/requests/${r.id}/claim`); setCurrent(r); load(); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const close = (acted) => {
    if (!acted && current?.flow.stage === "confirmed") staffApi().delete(`/pastoral/requests/${current.id}/claim`).finally(load);
    setCurrent(null);
  };
  const shown = rows.filter((r) => view === "all" || (view === "open" ? ["new", "proposed", "declined"].includes(r.flow.stage) : r.flow.stage === "confirmed"));
  return (
    <div className="space-y-4" data-testid="pastoral-requests">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-2xl font-bold tracking-tight text-slate-900">Pastoral Requests</h2>
          <p className="text-sm text-slate-500">Opening a request reserves its date for you, so two admins never work on the same date.</p></div>
        <div className="flex rounded-full bg-white p-1 shadow-sm">
          {[["open", "Open"], ["confirmed", "Confirmed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`pastoral-view-${k}`} className={`rounded-full px-4 py-1.5 text-sm ${view === k ? "bg-[#0A3A6B] text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-sm text-slate-400" data-testid="pastoral-empty">Nothing here right now.</p>}
      {shown.map((r) => <RequestCard key={r.id} r={r} me={me} onOpen={openReq} />)}
      <Dialog open={!!current} onOpenChange={(o) => !o && close(false)}>
        {current && <RequestDialog r={current} onClose={() => close(true)} reload={load} />}
      </Dialog>
    </div>
  );
};
